import { zodResolver } from '@hookform/resolvers/zod'
import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { StyledInput } from '~/components/elements/StyledInput'
import { useFormField } from '~/hooks/useFormField'
import { ensureExists } from '~/utils'

import { dialogInfoAtom } from '../atoms'
import { useCloseDialogWithRefresh } from '../hooks/useCloseDialogWithRefresh'
import { useCreateSurveyGroupMutation } from '../hooks/useCreateSurveyGroupMutation'
import { useUpdateSurveyGroupMutation } from '../hooks/useUpdateSurveyGroupMutation'

const schema = z.object({ title: z.string().min(1) })
type InputValues = z.infer<typeof schema>
const resolver = zodResolver(schema)

export const EditSurveyGroupDialog = () => {
  const { t } = useTranslation()
  const dialogInfo = useAtomValue(dialogInfoAtom)
  const { dialogType, surveyGroup } = ensureExists(
    dialogInfo,
    t('assertion_failed'),
  )
  const resetDialogInfo = useResetAtom(dialogInfoAtom)

  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
  } = useForm<InputValues>({
    defaultValues: { title: surveyGroup?.title ?? '' },
    resolver,
  })
  const titleField = useFormField(control, 'title')

  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const createSurveyGroupMutation = useCreateSurveyGroupMutation({
    onSuccess: closeDialogWithRefresh,
  })
  const updateSurveyGroupMutation = useUpdateSurveyGroupMutation({
    onSuccess: closeDialogWithRefresh,
  })

  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    ({ title }) => {
      if (!isDirty) {
        resetDialogInfo()

        return
      }

      if (dialogType === 'create') {
        createSurveyGroupMutation.mutate(title)
      } else {
        updateSurveyGroupMutation.mutate({
          surveyGroupId: surveyGroup.id,
          title,
        })
      }
    },
    [
      createSurveyGroupMutation,
      dialogType,
      isDirty,
      resetDialogInfo,
      surveyGroup,
      updateSurveyGroupMutation,
    ],
  )

  return (
    <ExecuteCancelDialog
      disabledExecute={!isValid}
      executing={
        createSurveyGroupMutation.isPending ||
        updateSurveyGroupMutation.isPending
      }
      mode={dialogType === 'create' ? 'create' : 'update'}
      title={`${dialogType === 'create' ? t('survey_group_create_add') : t('survey_group_create_update')}`}
      onCancel={resetDialogInfo}
      onExecute={handleSubmit(handleValid)}
    >
      <LabelingContainer label={t('survey_group_name')}>
        <StyledInput autoFocus={true} width="100%" {...titleField} />
      </LabelingContainer>
    </ExecuteCancelDialog>
  )
}
